import { useMutation, useFilesUpload, useImagesUpload } from '@obvio/app'
import { SubmitState } from '@obvio/ui'
import { cuid } from '@obvio/utils'

import { useArtworksContext } from './FormArtworksContext'

import type { Artwork } from './FormArtwork'
import type { ImageFileWithRatio } from '@obvio/app'

type FormData = {
  email: string
  phone: string
  name: string
  site: string
  social: string
  resume: any // FILE
  image: any // FILE
  video: string
  statement: string
  biogram: string
  exhibitions: [string, string][]
  awards: [string, string][]
  publications: [string, string][]
}

const normalizeMultiple = (data: [string, string][]): string[] =>
  data.map(([, b]) => b)

type ArtworkSubmit = {
  id: string
  title: string
  description: string
  images: any
  year: number
  features: { key: string; value: string | string[] }[]
}

const normalizeArtworks = (data: Artwork[]): ArtworkSubmit[] =>
  data.map(({ title, description, images, year, ...features }) => ({
    id: cuid(),
    title,
    description,
    images,
    year,
    features: Object.entries(features).map(([key, value]) => ({
      key,
      value: Array.isArray(value) ? value : [value],
    })),
  }))

export function useFormJoinSubmit(): (data: FormData) => Promise<SubmitState> {
  const { data: artworks } = useArtworksContext()
  const [fn] = useMutation('artistJoinForm')

  const uploadFile = useFilesUpload('MERCHANT')
  const uploadImage = useImagesUpload('MERCHANT')
  const uploadImageProduct = useImagesUpload('PRODUCT')

  return async (data: FormData) => {
    const id = cuid()
    const artworksList = normalizeArtworks([...artworks.values()])

    const cv = await uploadFile(id, async (register) =>
      register({ file: data.resume[0] }),
    )

    const image = await uploadImage(id, async (register) =>
      register(data.image[0]),
    )

    const artworksImages = await Promise.all(
      artworksList.map(async (artwork) => {
        const images = await uploadImageProduct(artwork.id, async (register) =>
          artwork.images.map((artworkImage: ImageFileWithRatio) =>
            register(artworkImage),
          ),
        )
        return { ...artwork, images }
      }),
    )

    void fn({
      id,
      name: data.name,
      contactMail: data.email,
      contactPhone: data.phone,
      description: data.statement,
      criticism: data.biogram,
      awards: normalizeMultiple(data.awards),
      press: normalizeMultiple(data.publications),
      exhibitions: normalizeMultiple(data.exhibitions),
      cv: [cv],
      image: [image],
      artworks: artworksImages,
      social: data.social,
      site: data.site,
      video: data.video,
    })

    return SubmitState.OK
  }
}
